import React, { useState } from 'react';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCamera, faChartColumn, faRepeat, faFaceSmile, faExpand,
    faMessage, faFilter, faEye, faHeart, faSparkles,
    faCheck, faXmark, faUsers, faVideo, faChartSimple, faChevronRight, faBadgeCheck, faMoon, faBell,
    faGhost,
} from '@fortawesome/pro-solid-svg-icons';
import './OffersModal.scss';
import Button, { BUTTON_VARIANTS } from '../UI/Button/Button';
import { useBreakpoint } from '../../../libs/breakpoint';
import createLink from '../../../libs/createLink';
import useCurrentUser from '../../../hooks/useCurrentUser';
import { isCertifEligibleGender } from '../../../const/certification';
import { startTrial, fetchPlansOffers } from '../../../api/public-voissa/plans';
import { USER_CURRENT_KEY } from '../../../api/public-voissa/users';
import { FEATURES } from '../../../const/features';
import {
    OFFERS, MAX_NAME, PLUS_NAME, CONTEXT_COPY, TRIGGER_COPY, PLUS_VITRINE, PLUS_VITRINE_EXTRA, VITRINE_CAP_ALIAS,
    PLUS_CARD_FEATURES, MAX_CARD_FEATURES, MAX_PANEL,
    CERTIF_PANEL, CERTIF_EYEBROW, CERTIF_SUBHEADLINE, CERTIF_NOTE, CERTIF_HEADLINE, CERTIF_HEADLINE_DEFAULT,
} from './offersContent';

// Modale d'offres CENTRALISÉE — remplace ContentLocked / TrialModal / StatsPremiumGate.
// Reproduit les 5 gabarits validés (vitrine de features, hero Max, panneau « Inclus », comparatif).
export const OFFERS_MODAL_CONTEXT = {
    LAUNCH: 'launch',
    BLOCKED: 'blocked',
    TRIAL_END: 'trial-end',
};

const TRIAL_PLUS_SLUG = 'voissa-plus';

const ICONS = {
    camera: faCamera, chart: faChartColumn, repeat: faRepeat, smile: faFaceSmile, expand: faExpand,
    message: faMessage, filter: faFilter, eye: faEye, heart: faHeart, sparkles: faSparkles,
    check: faCheck, users: faUsers, video: faVideo, stats: faChartSimple, badge: faBadgeCheck, moon: faMoon, bell: faBell,
    incognito: faGhost,
};

// Prix réels via GET /plans/offers (source de vérité vMessenger, réductions user incluses ;
// jamais hardcodés). On prend l'offre d'entrée (total le plus bas) et son prix/mois.
// Renvoie { plus, max } en euros (ou null si vMessenger indisponible).
const usePlanPrices = () => {
    const { data } = useQuery(['plans-offers'], () => fetchPlansOffers(), {
        staleTime: 1000 * 60 * 30,
        select: (res) => {
            // Défaut fail-open : si l'API ne répond pas, on ne verrouille pas Voissa Plus.
            if (res?.status !== 'success') return { plus: null, max: null, plusOffer: null, maxOffer: null, plusAvailable: true, plusAvailableFrom: null };
            // Offre d'entrée = total le plus bas (= 1 mois). On en tire le prix/mois ET le slug
            // vMessenger (transmis au paiement pour cibler l'offre). Décision : PAS de sélecteur
            // de durée dans la modale — le choix 1/3/6 mois se fait sur la plateforme de paiement.
            const entryOffer = (group) => {
                const offers = (group?.offers || []).filter((o) => typeof o?.price === 'number' && o.price > 0);
                if (!offers.length) return null;
                return offers.reduce((a, b) => (b.price < a.price ? b : a));
            };
            const perMonth = (entry) => {
                if (!entry) return null;
                const value = typeof entry?.pricePerMonth === 'number' ? entry.pricePerMonth : entry?.price;
                return typeof value === 'number' && value > 0 ? value : null;
            };
            const plusEntry = entryOffer(res.plus);
            const maxEntry = entryOffer(res.premium);
            return {
                plus: perMonth(plusEntry),
                max: perMonth(maxEntry),
                plusOffer: plusEntry?.slug || null,
                maxOffer: maxEntry?.slug || null,
                // Fenêtre d'exclusivité Premium au lancement : abo payant Plus fermé jusqu'à cette date.
                plusAvailable: res.plus?.isAvailable !== false,
                plusAvailableFrom: res.plus?.availableFrom || null,
            };
        },
    });
    return data || { plus: null, max: null, plusOffer: null, maxOffer: null, plusAvailable: true, plusAvailableFrom: null };
};

// On n'affiche jamais 0,00 € : prix <= 0 (ou invalide) => null (rien affiché).
const fmtPrice = (v) => (typeof v === 'number' && v > 0 ? `${v.toFixed(2).replace('.', ',')} €` : null);

const Icon = ({ name, className }) => (ICONS[name] ? <FontAwesomeIcon icon={ICONS[name]} className={className} /> : null);

// Titre : segments d'index impair en dégradé. Le pseudo est fusionné dans le 1er segment (parité).
const GradientHeadline = ({ headline, username }) => {
    let parts = Array.isArray(headline) ? headline : [headline];
    if (username && parts.length) parts = [`${username}, ${parts[0]}`, ...parts.slice(1)];
    return (
        <div className="om-title">
            {parts.map((p, i) => (i % 2 === 1 ? <span key={i} className="om-grad">{p}</span> : <React.Fragment key={i}>{p}</React.Fragment>))}
        </div>
    );
};

// Vitrine 5 tuiles (Voissa Plus). activeCap = tuile mise en avant (les autres atténuées).
// Les features Plus hors maquette (stats / mode sombre) ont leur tuile dans PLUS_VITRINE_EXTRA :
// quand elles déclenchent la modale, elle remplace le dernier emplacement (grille figée à 5).
const Vitrine = ({ activeCap = null }) => {
    // Alias : certains triggers highlightent une tuile existante (lecture stories -> tuile Stories).
    const capKey = VITRINE_CAP_ALIAS[activeCap] || activeCap;
    const extra = PLUS_VITRINE_EXTRA[capKey];
    const tiles = extra ? [...PLUS_VITRINE.slice(0, -1), extra] : PLUS_VITRINE;
    const highlightCap = tiles.some((f) => f.cap === capKey) ? capKey : null;
    return (
    <div className="om-vitrine">
        {tiles.map((f) => {
            const active = !highlightCap || f.cap === highlightCap;
            return (
                <div key={f.cap} className="om-vfeat">
                    <div className={classNames('om-tile', { 'is-active': highlightCap && active, 'is-dim': highlightCap && !active })}>
                        <Icon name={f.icon} />
                    </div>
                    <div className={classNames('om-vlabel', { 'is-dim': highlightCap && !active })}>{f.label}</div>
                </div>
            );
        })}
    </div>
    );
};

const PriceLine = ({ value, suffix = ' /mois', align = 'left' }) => {
    const p = fmtPrice(value);
    if (!p) return null;
    return (
        <div className={classNames('om-price', `om-price--${align}`)}>
            <span className="om-price-value">{p}</span><span className="om-price-suffix">{suffix}</span>
        </div>
    );
};

const OffersModal = ({ context = OFFERS_MODAL_CONTEXT.LAUNCH, triggerAction = null, username, canBePriviliged = false, isCertified = false, inDrawer = false, fullWidth = false, handleCancel = () => {} }) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const currentUser = useCurrentUser();
    const prices = usePlanPrices();
    const breakpoints = useBreakpoint();
    const isMobile = !!(breakpoints?.xs || breakpoints?.sm); // bottom-sheet
    const [submitting, setSubmitting] = useState(false);
    // Motif d'échec de l'activation d'essai, affiché sous le CTA (cf. doStartTrial).
    const [trialError, setTrialError] = useState(null);
    // Bascule interne vers le COMPARATIF d'offres (gabarit compact du lancement) : déclenchée par
    // la ligne Voissa Plus du gabarit bloqué Max — on présente l'offre au lieu d'agir directement.
    const [showOffers, setShowOffers] = useState(false);
    // Fenêtre d'exclusivité (compact) : Premium est la carte déployée, mais le contenu de Voissa
    // Plus reste CONSULTABLE — le clic sur sa ligne déploie sa carte teaser (achat indisponible).
    const [showPlusDetail, setShowPlusDetail] = useState(false);

    const canStartTrial = !!currentUser?.subscription?.canStartTrial;
    // Plan actif détenu (voissa-plus | voissa-max, essai inclus) — sert à ne pas revendre à un
    // membre ce qu'il possède déjà. `null` pour un gratuit comme pour un certifié.
    const hasActivePlan = !!currentUser?.subscription?.plan;

    // Fenêtre d'exclusivité Premium au lancement : l'ABONNEMENT PAYANT Voissa Plus est fermé
    // jusqu'à la date d'ouverture (source : GET /plans/offers, cf. const/plus.js côté API).
    // L'essai gratuit n'est PAS concerné : seul le CTA payant est grisé.
    const plusLocked = !prices.plusAvailable;
    const plusOpenLabel = prices.plusAvailableFrom
        ? new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long' }).format(new Date(prices.plusAvailableFrom))
        : null;
    const plusLockNote = plusOpenLabel ? `Disponible à partir du ${plusOpenLabel}` : 'Bientôt disponible';
    const name = username || currentUser?.username;

    // État « Plus indisponible » : fenêtre fermée ET pas d'essai possible. Premium devient alors
    // l'offre mise en avant (carte vedette, CTA plein) et Plus passe en TEASER daté — on ne vend
    // jamais un essai inexistant ni un abo fermé.
    const plusUnavailable = plusLocked && !canStartTrial;

    const trigger = context === OFFERS_MODAL_CONTEXT.BLOCKED ? (TRIGGER_COPY[triggerAction] || null) : null;
    // Vue comparatif (clic sur la ligne Voissa Plus) : tête du lancement, pas celle du blocage.
    const baseHead = showOffers ? CONTEXT_COPY.launch : (trigger || CONTEXT_COPY[context] || CONTEXT_COPY.launch);
    const tier = trigger?.tier || null;

    // Toute restriction Plus (tier ≠ max, y compris trigger inconnu) présente le COMPARATIF des
    // deux offres : une action bloquée doit montrer les offres et leurs avantages, pas un CTA sec.
    const isBlockedPlus = context === OFFERS_MODAL_CONTEXT.BLOCKED && tier !== 'max';

    // Copy dédiée à l'état « Plus indisponible » sur les gabarits comparatif : annonce de la date
    // d'ouverture + bascule du pitch sur Premium. (Le gabarit bloqué Max garde sa tête : elle vend
    // déjà Premium.)
    const LOCKED_HEADS = {
        [OFFERS_MODAL_CONTEXT.LAUNCH]: {
            eyebrow: 'Bientôt sur Voissa',
            headline: ['Voissa Plus ouvre le ', plusOpenLabel || 'bientôt'],
            subheadline: `En attendant, ${MAX_NAME} inclut déjà tout ${PLUS_NAME} : stories, sondages, réactions et bien plus encore.`,
        },
        [OFFERS_MODAL_CONTEXT.TRIAL_END]: {
            eyebrow: 'Votre essai est terminé',
            headline: ['Gardez tous vos avantages avec ', MAX_NAME],
            subheadline: plusOpenLabel
                ? `L'abonnement ${PLUS_NAME} ouvre le ${plusOpenLabel}. ${MAX_NAME} vous débloque tout dès maintenant.`
                : `${MAX_NAME} vous débloque tout dès maintenant.`,
        },
        [OFFERS_MODAL_CONTEXT.BLOCKED]: {
            eyebrow: 'Bientôt disponible',
            headline: ['Voissa Plus ouvre le ', plusOpenLabel || 'bientôt'],
            subheadline: `En attendant, ${MAX_NAME} débloque cette fonctionnalité et tout le reste dès aujourd'hui.`,
        },
    };
    const onComparatif = showOffers || context === OFFERS_MODAL_CONTEXT.LAUNCH || context === OFFERS_MODAL_CONTEXT.TRIAL_END || isBlockedPlus;
    const head = plusUnavailable && onComparatif
        ? LOCKED_HEADS[showOffers ? OFFERS_MODAL_CONTEXT.LAUNCH : context]
        : baseHead;

    // Alternative CERTIFICATION (gratuite, profils femme/couple) : proposée uniquement quand la
    // certif accorde réellement la capability bloquée (`certifiable` = relationnel + quotas —
    // jamais la création ni les stats). `canBePriviliged` vient de useRestriction quand la modale
    // est ouverte par lui ; sinon fallback sur le genre du profil (même critère).
    const certifEligible = !!trigger?.certifiable
        && (canBePriviliged || isCertifEligibleGender(currentUser?.profile?.gender));
    const certified = isCertified || !!currentUser?.isCertified;

    // Redirige vers /paiement en transmettant le palier visé via `plan` et, optionnellement,
    // l'offre sélectionnée via `offer` (slug vMessenger) — récupérés côté paiement pour cibler
    // l'offre. Voissa Max = palier premium par défaut → `plan` laissé vide. `offerSlug` absent =>
    // non transmis (vMessenger retombe sur son offre par défaut, 1 mois).
    const goSubscribe = (slug, offerSlug = null) => {
        handleCancel?.();
        const plan = slug === OFFERS.max.slug ? '' : slug;
        const params = new URLSearchParams();
        if (plan) params.set('plan', plan);
        if (offerSlug) params.set('offer', offerSlug);
        const qs = params.toString();
        navigate(qs ? `${createLink('subscription')}?${qs}` : createLink('subscription'));
    };
    const goCertify = () => { handleCancel?.(); navigate(createLink('userMeSettingsCertification', currentUser)); };
    // `POST /users/me/trial` refuse pour 8 motifs distincts (plan déjà actif, campagne fermée,
    // rôle, ancienneté du compte, activité récente, seuil de suiveurs, audience, essai déjà
    // consommé) — dont plusieurs que le front ne peut PAS pré-calculer : `canStartTrial` est
    // résolu au dernier /users/me et dépend d'un comptage de suiveurs en base. Fermer la modale
    // sans un mot laissait le membre croire que son essai avait démarré. On garde donc la modale
    // ouverte et on affiche le motif du serveur, écrit pour le membre.
    const TRIAL_ERROR_FALLBACK = "Impossible d'activer l'essai pour le moment. Réessayez dans quelques instants.";

    const doStartTrial = async () => {
        if (submitting) return;
        setSubmitting(true);
        setTrialError(null);
        try {
            await startTrial(TRIAL_PLUS_SLUG);
            await queryClient.invalidateQueries([USER_CURRENT_KEY]);
            setSubmitting(false);
            handleCancel?.();
        } catch (e) {
            // On rafraîchit quand même : si le refus vient d'un plan déjà actif, la modale doit
            // refléter le vrai état (canStartTrial repasse à false, la carte bascule sur l'abo).
            await queryClient.invalidateQueries([USER_CURRENT_KEY]);
            // Seuls les refus métier (403 / 409) portent un message rédigé pour le membre ;
            // le reste (réseau, 500, timeout) ne doit jamais être affiché tel quel.
            const isBusinessRefusal = [403, 409].includes(e?.statusCode) && !!e?.message;
            setTrialError(isBusinessRefusal ? e.message : TRIAL_ERROR_FALLBACK);
            setSubmitting(false);
        }
    };

    // Message d'échec sous le CTA d'essai (les deux gabarits portent le même bouton).
    const TrialError = () => (trialError ? <div className="om-card-error" role="alert">{trialError}</div> : null);

    const closeBtn = inDrawer ? (
        <button type="button" className="om-close" aria-label="Fermer" onClick={handleCancel}><FontAwesomeIcon icon={faXmark} /></button>
    ) : null;

    const Head = ({ heroIcon }) => (
        <div className="om-head">
            {heroIcon && <div className="om-hero"><Icon name={heroIcon} /></div>}
            {head.eyebrow && <div className="om-eyebrow">{head.eyebrow}</div>}
            <GradientHeadline headline={head.headline} username={(showOffers || context !== OFFERS_MODAL_CONTEXT.BLOCKED) ? name : undefined} />
            {head.subheadline && <div className="om-sub">{head.subheadline}</div>}
        </div>
    );

    // Pied sans lien « Comparer » (la modale EST déjà le comparateur d'offres) : une note
    // éventuelle à gauche, « Plus tard » en drawer — centré quand il est seul.
    const Footer = ({ note = null }) => ((note || inDrawer) ? (
        <div className={classNames('om-foot', { 'om-foot--solo': !note })}>
            {note && <span className="om-foot-note">{note}</span>}
            {inDrawer && <button type="button" className="om-dismiss" onClick={handleCancel}>Plus tard</button>}
        </div>
    ) : null);

    // Carte Plus (comparatif launch / trial-end). Abo fermé sans essai possible : la carte passe
    // en TEASER « Bientôt » — contenu lisible adouci, la date en pastille, aucun bouton mort.
    const PlusCard = () => {
        const trial = canStartTrial;
        const isTrialEnd = context === OFFERS_MODAL_CONTEXT.TRIAL_END;
        const paidLocked = plusLocked && !trial; // abo payant fermé (l'essai reste possible)
        return (
            <div className={classNames('om-card om-card--plus', { 'om-card--soon': paidLocked })}>
                {paidLocked
                    ? <span className="om-badge om-badge--soft">Bientôt</span>
                    : <span className="om-badge om-badge--grad">{isTrialEnd ? 'Votre offre' : (trial ? '30 jours offerts' : 'Nouveau')}</span>}
                <div className="om-card-name om-grad">{OFFERS.plus.name}</div>
                <div className="om-card-tagline">{isTrialEnd ? 'Tout ce que vous aimez, conservé.' : 'Le social, débloqué.'}</div>
                <PriceLine value={prices.plus} suffix={trial && !isTrialEnd ? ' /mois ensuite' : ' /mois'} />
                <ul className="om-list">
                    {PLUS_CARD_FEATURES.map((f, i) => <li key={i}><Icon name="check" className="om-li-icon om-li-icon--accent" />{f}</li>)}
                </ul>
                {paidLocked ? (
                    <div className="om-card-soon-pill">{plusLockNote}</div>
                ) : (
                    <>
                        <Button variant={BUTTON_VARIANTS.PINK_GRADIENT} full loading={submitting && trial}
                            onClick={trial ? doStartTrial : () => goSubscribe(OFFERS.plus.slug, prices.plusOffer)}>
                            {isTrialEnd ? 'Continuer en Voissa Plus' : (trial ? "J'active mon essai gratuit" : 'Découvrir Voissa Plus')}
                        </Button>
                        <TrialError />
                        <div className="om-card-note">{trial && !isTrialEnd ? 'Sans carte bancaire · sans engagement' : 'Sans engagement · résiliable à tout moment'}</div>
                    </>
                )}
            </div>
        );
    };

    // Carte Max (comparatif). `featured` (Plus indisponible) : Premium devient l'offre vedette —
    // bordure forte, badge dégradé « Disponible aujourd'hui », CTA plein.
    const MaxCard = ({ featured = false }) => (
        <div className={classNames('om-card om-card--max', { 'om-card--featured': featured })}>
            <span className={classNames('om-badge', featured ? 'om-badge--grad' : 'om-badge--soft')}>{featured ? "Disponible aujourd'hui" : 'Le plus complet'}</span>
            <div className="om-card-name">{OFFERS.max.name}</div>
            <div className="om-card-tagline">Tout Plus, et bien plus.</div>
            <PriceLine value={prices.max} suffix=" /mois" />
            <ul className="om-list">
                {MAX_CARD_FEATURES.map((f, i) => (
                    <li key={i}><Icon name={f.icon} className="om-li-icon om-li-icon--muted" /><span className={classNames({ 'om-li-strong': f.strong })}>{f.label}</span></li>
                ))}
            </ul>
            <Button variant={featured ? BUTTON_VARIANTS.PINK_GRADIENT : BUTTON_VARIANTS.OUTLINE} full onClick={() => goSubscribe(OFFERS.max.slug, prices.maxOffer)}>Passer à {MAX_NAME}</Button>
        </div>
    );

    // Bandeau « certification gratuite » (bloqué Plus, quotas) : alternative à l'abonnement pour
    // les profils éligibles — la certification vidéo accorde aussi la capability bloquée.
    const CertifRow = () => (
        <div className="om-certif-row">
            <Icon name="badge" className="om-certif-icon" />
            <span className="om-certif-text">
                <b>Profil femme ou couple&nbsp;?</b> La certification vidéo <b>gratuite</b> lève aussi cette limite.
            </span>
            <button type="button" className="om-certif-btn" onClick={goCertify}>
                {certified ? 'Re-certifier mon profil' : 'Certifier mon profil'}
            </button>
        </div>
    );

    // Ligne Voissa Plus compacte (gabarit bloqué Max) : la restriction montre AUSSI l'autre offre.
    // Quand l'offre est activable, le clic OUVRE le comparatif d'offres (gabarit compact) — on ne
    // démarre jamais l'essai ni le paiement depuis la ligne. Fenêtre d'exclusivité sans essai =>
    // ligne adoucie + date, mais toujours CLIQUABLE : elle déploie la carte teaser de Voissa Plus
    // (le contenu reste consultable, seul l'achat est indisponible).
    const PlusRow = () => {
        // Le membre détient déjà un plan (Voissa Plus ou Max, ESSAI INCLUS — le trial pose
        // subscription.plan) : lui reproposer l'offre qu'il a déjà, sur une restriction Premium,
        // est incompréhensible. Un certifié, lui, garde la ligne : la certif n'ouvre pas la
        // création, Voissa Plus lui apporte donc bien quelque chose.
        if (hasActivePlan) return null;

        const locked = plusLocked && !canStartTrial;
        const plusPrice = fmtPrice(prices.plus);
        return (
            <button type="button"
                className={classNames('om-max-row om-plus-row', { 'om-max-row--locked': locked })}
                onClick={() => { setShowOffers(true); setShowPlusDetail(locked); }}>
                <span className="om-max-row-text">
                    <span className="om-max-row-name">{OFFERS.plus.name}</span>
                    <span className="om-max-row-sub">{locked ? plusLockNote : 'Stories, sondages, réactions, stats, mode sombre'}</span>
                </span>
                <span className="om-max-row-price">
                    {canStartTrial
                        ? <span className="om-plus-row-trial om-grad">30 j offerts</span>
                        : (plusPrice && <span className="om-price-value">{plusPrice}</span>)}
                    <FontAwesomeIcon icon={faChevronRight} className="om-max-row-chev" />
                </span>
            </button>
        );
    };

    let body;

    // 1a) Gabarit COMPACT (carte Plus déployée + ligne Max compacte, maquette validée) :
    // lancement & bloqué Plus sur MOBILE (bottom-sheet), et vue comparatif `showOffers`
    // (clic sur la ligne Voissa Plus du gabarit bloqué Max — drawer étroit, même sur desktop).
    // En bloqué, la tête est contextuelle et la tuile active.
    if (((context === OFFERS_MODAL_CONTEXT.LAUNCH || isBlockedPlus) && isMobile) || showOffers) {
        const plusPrice = fmtPrice(prices.plus);
        const maxPrice = fmtPrice(prices.max);
        body = (
            <>
                <Head />
                <Vitrine activeCap={isBlockedPlus ? triggerAction : null} />
                {isBlockedPlus && certifEligible && <CertifRow />}
                <div className="om-compact">
                    {plusUnavailable ? (showPlusDetail ? (
                        <>
                            {/* Détail Voissa Plus consulté pendant la fenêtre : carte TEASER
                                (contenu lisible, date en pastille, aucun CTA) + Premium en ligne. */}
                            <div className="om-pc om-pc--soon">
                                <span className="om-badge om-badge--soft">Bientôt</span>
                                <div className="om-pc-top">
                                    <span className="om-card-name om-grad">{OFFERS.plus.name}</span>
                                    {plusPrice && (
                                        <span className="om-pc-price">
                                            <span className="om-price-value">{plusPrice}</span>
                                            <span className="om-price-suffix"> /mois</span>
                                        </span>
                                    )}
                                </div>
                                <div className="om-pc-feats">
                                    {['Stories', 'Sondages', 'Repartage', 'Réactions', 'Plein écran', 'Stats', 'Mode sombre', 'Badge argent'].map((f) => (
                                        <div key={f} className="om-pc-chk"><Icon name="check" className="om-li-icon om-li-icon--accent" />{f}</div>
                                    ))}
                                </div>
                                <div className="om-card-soon-pill">{plusLockNote}</div>
                            </div>
                            <button type="button" className="om-max-row" onClick={() => goSubscribe(OFFERS.max.slug, prices.maxOffer)}>
                                <span className="om-max-row-text">
                                    <span className="om-max-row-name">{OFFERS.max.name}</span>
                                    <span className="om-max-row-sub">Disponible aujourd'hui · tout Plus inclus</span>
                                </span>
                                <span className="om-max-row-price">
                                    {maxPrice && <span className="om-price-value">{maxPrice}</span>}
                                    <FontAwesomeIcon icon={faChevronRight} className="om-max-row-chev" />
                                </span>
                            </button>
                        </>
                    ) : (
                        <>
                            {/* Plus indisponible : Premium devient la carte déployée, Plus passe en
                                teaser daté (ligne cliquable pour consulter son contenu). */}
                            <div className="om-pc">
                                <span className="om-badge om-badge--grad">Disponible aujourd'hui</span>
                                <div className="om-pc-top">
                                    <span className="om-card-name">{OFFERS.max.name}</span>
                                    {maxPrice && (
                                        <span className="om-pc-price">
                                            <span className="om-price-value">{maxPrice}</span>
                                            <span className="om-price-suffix"> /mois</span>
                                        </span>
                                    )}
                                </div>
                                <div className="om-pc-feats">
                                    {['Tout Voissa Plus', 'Vmessenger illimité', 'Filtre des membres', 'Tous vos visiteurs', 'Votre cercle privé', ...(FEATURES.INCOGNITO ? ['Mode incognito'] : []), 'Badge gold'].map((f) => (
                                        <div key={f} className="om-pc-chk"><Icon name="check" className="om-li-icon om-li-icon--accent" />{f}</div>
                                    ))}
                                </div>
                                <Button variant={BUTTON_VARIANTS.PINK_GRADIENT} big full onClick={() => goSubscribe(OFFERS.max.slug, prices.maxOffer)}>
                                    Passer à {MAX_NAME}
                                </Button>
                            </div>
                            <PlusRow />
                        </>
                    )) : (
                        <>
                            <div className="om-pc">
                                <span className="om-badge om-badge--grad">{canStartTrial ? '30 jours offerts' : 'Nouveau'}</span>
                                <div className="om-pc-top">
                                    <span className="om-card-name om-grad">{OFFERS.plus.name}</span>
                                    {plusPrice && (
                                        <span className="om-pc-price">
                                            <span className="om-price-value">{plusPrice}</span>
                                            <span className="om-price-suffix">{canStartTrial ? ' /mois ensuite' : ' /mois'}</span>
                                        </span>
                                    )}
                                </div>
                                <div className="om-pc-feats">
                                    {['Stories', 'Sondages', 'Repartage', 'Réactions', 'Plein écran', 'Stats', 'Mode sombre', 'Badge argent'].map((f) => (
                                        <div key={f} className="om-pc-chk"><Icon name="check" className="om-li-icon om-li-icon--accent" />{f}</div>
                                    ))}
                                </div>
                                <Button variant={BUTTON_VARIANTS.PINK_GRADIENT} big full loading={submitting && canStartTrial}
                                    onClick={canStartTrial ? doStartTrial : () => goSubscribe(OFFERS.plus.slug, prices.plusOffer)}>
                                    {canStartTrial ? "J'active mon essai" : 'Découvrir Voissa Plus'}
                                </Button>
                                <TrialError />
                                <div className="om-card-note">{canStartTrial ? 'Sans carte bancaire ni engagement' : 'Sans engagement · résiliable à tout moment'}</div>
                            </div>
                            <button type="button" className="om-max-row" onClick={() => goSubscribe(OFFERS.max.slug, prices.maxOffer)}>
                                <span className="om-max-row-text">
                                    <span className="om-max-row-name">{OFFERS.max.name}</span>
                                    <span className="om-max-row-sub">Tout Plus + Vmessenger illimité</span>
                                </span>
                                <span className="om-max-row-price">
                                    {maxPrice && <span className="om-price-value">{maxPrice}</span>}
                                    <FontAwesomeIcon icon={faChevronRight} className="om-max-row-chev" />
                                </span>
                            </button>
                        </>
                    )}
                </div>
                {inDrawer && <div className="om-compact-foot"><button type="button" className="om-dismiss" onClick={handleCancel}>Plus tard</button></div>}
            </>
        );
    // 1b) Desktop — lancement, fin d'essai & bloqué Plus : tête + vitrine (sauf fin d'essai) +
    // 2 cartes comparatif. Abo payant Plus fermé (fenêtre d'exclusivité) sans essai possible =>
    // carte Plus grisée, Premium reste l'offre actionnable.
    } else if (context === OFFERS_MODAL_CONTEXT.LAUNCH || context === OFFERS_MODAL_CONTEXT.TRIAL_END || isBlockedPlus) {
        body = (
            <>
                <Head />
                {context !== OFFERS_MODAL_CONTEXT.TRIAL_END && <Vitrine activeCap={isBlockedPlus ? triggerAction : null} />}
                {isBlockedPlus && certifEligible && <CertifRow />}
                <div className="om-cards">
                    {plusUnavailable ? <><MaxCard featured /><PlusCard /></> : <><PlusCard /><MaxCard /></>}
                </div>
                <Footer note={context === OFFERS_MODAL_CONTEXT.TRIAL_END ? 'Sinon, vous repassez en accès gratuit.' : null} />
            </>
        );
    } else if (canBePriviliged || certifEligible) {
        // 2a) Action bloquée Max + profil ÉLIGIBLE À LA CERTIFICATION. La certif vidéo accorde
        // exactement les capabilities relationnelles qui bloquent l'action, et elle est GRATUITE :
        // on vend donc la certification (ce qu'elle débloque + CTA principal), Premium ne subsiste
        // qu'en lien secondaire. Pas de prix affiché au-dessus du CTA (il est gratuit), et pas de
        // ligne Voissa Plus : Plus n'ouvre pas le relationnel, il ne débloquerait pas l'action.
        const maxPrice = fmtPrice(prices.max);
        body = (
            <>
                <div className="om-head">
                    <div className="om-hero"><Icon name="badge" /></div>
                    <div className="om-eyebrow">{CERTIF_EYEBROW}</div>
                    <GradientHeadline headline={CERTIF_HEADLINE[triggerAction] || CERTIF_HEADLINE_DEFAULT} />
                    <div className="om-sub">{CERTIF_SUBHEADLINE}</div>
                </div>
                <div className="om-panel-wrap">
                    <div className="om-panel">
                        <div className="om-panel-label">Débloqué par la certification</div>
                        {CERTIF_PANEL.map((it, i) => {
                            const active = it.cap === triggerAction;
                            return (
                                <div key={i} className={classNames('om-pli', { 'is-active': active })}>
                                    <Icon name={it.icon} className="om-pli-icon" />
                                    <span className={classNames({ 'om-li-strong': active })}>{it.label}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className="om-action">
                    <Button variant={BUTTON_VARIANTS.PINK_GRADIENT} big full onClick={goCertify}>
                        {certified ? 'Re-certifier mon profil' : 'Certifier mon profil'}
                    </Button>
                    <div className="om-action-note">{CERTIF_NOTE}</div>
                    <button type="button" className="om-card-link" onClick={() => goSubscribe(OFFERS.max.slug, prices.maxOffer)}>
                        {maxPrice ? `Ou passer à ${MAX_NAME} — ${maxPrice} /mois` : `Ou passer à ${MAX_NAME}`}
                    </button>
                </div>
                <Footer />
            </>
        );
    } else {
        // 2b) Action bloquée — Voissa Max, profil NON éligible à la certification : hero cercle +
        // panneau « Inclus » + prix + CTA, suivi de la ligne Voissa Plus compacte (les DEUX offres
        // sont toujours présentées).
        const lead = trigger?.lead || null;
        const items = lead ? [{ ...lead, active: true }, ...MAX_PANEL] : MAX_PANEL;
        body = (
            <>
                <Head heroIcon={trigger?.hero || 'message'} />
                <div className="om-panel-wrap">
                    <div className="om-panel">
                        <div className="om-panel-label">Inclus dans {MAX_NAME}</div>
                        {items.map((it, i) => {
                            const active = it.active || it.cap === triggerAction;
                            return (
                                <div key={i} className={classNames('om-pli', { 'is-active': active })}>
                                    <Icon name={it.icon} className="om-pli-icon" />
                                    <span className={classNames({ 'om-li-strong': active })}>{it.label}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className="om-action">
                    <PriceLine value={prices.max} suffix=" /mois" align="center" />
                    <Button variant={BUTTON_VARIANTS.PINK_GRADIENT} big full
                        onClick={() => goSubscribe(OFFERS.max.slug, prices.maxOffer)}>
                        Passer à {MAX_NAME}
                    </Button>
                    <div className="om-action-note">Sans engagement · résiliable à tout moment</div>
                </div>
                <div className="om-plus-row-wrap"><PlusRow /></div>
                <Footer />
            </>
        );
    }

    // `wide` (max-width 100%) : auto pour tous les gabarits à comparatif (lancement, fin d'essai,
    // bloqué Plus, vue comparatif), ou forcé via `fullWidth` pour les gabarits mono-colonne qu'on
    // veut voir occuper toute la largeur du drawer (ex. CTA des statistiques).
    const wide = fullWidth || showOffers || context === OFFERS_MODAL_CONTEXT.LAUNCH || context === OFFERS_MODAL_CONTEXT.TRIAL_END || isBlockedPlus;

    return (
        <div className={classNames('om', { 'om--wide': wide, 'om--drawer': inDrawer })}>
            {closeBtn}
            {body}
        </div>
    );
};

export default OffersModal;
